// Helpers purs d'affichage (texte + dates), extraits d'App.tsx.

// Rend un texte avec URLs cliquables + mentions surlignées (sans dangerouslySetInnerHTML).
// Les mentions WhatsApp arrivent du bridge en TEXTE NU = le nom du membre (sans « @ »,
// ex. « Axel Corton (WA) »). On surligne donc les noms des membres du groupe présents
// dans le corps (passés via `mentionNames`), + un « @Nom » explicite par sécurité.
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const AT_MENTION =
  "(?<=^|\\s)@[\\p{L}\\p{N}][\\p{L}\\p{N}'’.\\-]*(?:\\s\\p{Lu}[\\p{L}'’.\\-]*){0,2}";

export function linkify(
  text: string,
  mentionNames: string[] = [],
  onMention?: (name: string) => void,
) {
  // Noms de membres assez spécifiques pour éviter les faux positifs (≥ 2 caractères),
  // triés du plus long au plus court pour matcher « Axel Corton » avant « Axel ».
  const names = [...new Set(mentionNames.filter((n) => n && n.trim().length >= 2))].sort(
    (a, b) => b.length - a.length,
  );
  const nameSet = new Set(names);
  // URL: stop before whitespace AND angle brackets (Slack/markdown wrap links as
  // <https://…> / <https://…|label>), and never end on trailing punctuation
  // (a closing «.,;:!?)]}'"» glued to the URL belongs to the sentence, not the
  // link). Without this the autolink swallowed the closing ">" → broken hrefs.
  const URL_PATTERN = "https?:\\/\\/[^\\s<>|]*[^\\s<>|.,;:!?)\\]}'\"»]";
  const patterns = [URL_PATTERN, AT_MENTION];
  if (names.length) patterns.push(names.map(escapeRe).join("|"));
  const re = new RegExp(`(${patterns.join("|")})`, "gu");
  return text.split(re).map((part, i) => {
    if (!part) return null;
    if (/^https?:\/\//.test(part))
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="msg-link">
          {part}
        </a>
      );
    if (part.startsWith("@") || nameSet.has(part))
      return (
        <span
          key={i}
          className="mention"
          role={onMention ? "button" : undefined}
          tabIndex={onMention ? 0 : undefined}
          onClick={
            onMention
              ? (e) => {
                  e.stopPropagation();
                  onMention(part.replace(/^@/, "").trim());
                }
              : undefined
          }
        >
          {part}
        </span>
      );
    return part;
  });
}

// --- small date helpers ----------------------------------------------------

function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
export function relTime(s: string | null | undefined): string {
  const d = parseDate(s);
  if (!d) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}
export function clockTime(s: string | null | undefined): string {
  const d = parseDate(s);
  return d ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
}
export function dayLabel(s: string | null | undefined): string {
  const d = parseDate(s);
  if (!d) return "";
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === y.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
}
